import Decimal from 'decimal.js-light';
import { Datatype, type FieldDatatype } from '../../../datatype/Datatype.ts';

/**
 * Utility di coercizione numerica per gli operatori di confronto delle
 * condizioni (greaterThan, lowerThan, equals, notEquals).
 *
 * Il problema: un campo di tipo `Datatype.Number` può arrivare al confronto sia
 * come `number` (widget Number, schema `yup.number()`) sia come stringa (widget
 * Currency, che dichiara Number ma salva `yup.string()`). Confrontare stringhe
 * numeriche con gli operatori nativi `>`/`<` produrrebbe un confronto
 * alfabetico, quindi serve convertirle a numero.
 *
 * Questo modulo espone solo la coercizione (verso `Decimal`, che evita gli
 * errori di virgola mobile) e il predicato che stabilisce quando applicarla. La
 * semantica del confronto vero e proprio (`>`, `<`, `=`) resta dentro ogni
 * operatore, in modo simmetrico al ramo che gestisce le date.
 */

/**
 * Indica se il confronto va eseguito con coercizione numerica via Decimal.
 * Serve solo quando il campo è di tipo Number e almeno un operando è una
 * stringa: in quel caso il confronto nativo `>`/`<` sarebbe alfabetico. Per number puri il
 * confronto nativo è già corretto e non serve coercizione.
 */
export function shouldCompareAsNumber(
  field: FieldDatatype,
  value: unknown,
  target: unknown,
) {
  return (
    field.type === Datatype.Number &&
    (typeof value === 'string' || typeof target === 'string')
  );
}

/**
 * Converte un valore in Decimal, oppure null se nullo o non parsabile.
 *
 * Il try/catch è necessario perché `new Decimal(...)` di decimal.js-light NON
 * restituisce un valore non finito sulle stringhe non numeriche: lancia un'eccezione.
 * Il controllo `Number.isFinite` da solo intercetterebbe solo il NaN numerico,
 * non la stringa non parsabile.
 *
 * Senza il catch l'eccezione si propagherebbe fino a
 * `ConditionExpression.evaluate`, che la rilancia interrompendo la valutazione
 * dell'intera condizione, e vanificherebbe i fallback voluti dai singoli
 * operatori (es. equals/notEquals, che sui valori non parsabili ricadono sul
 * confronto letterale). Ritornando null lasciamo all'operatore la decisione,
 * come già fatto in ComputeMathListFunctions.
 */
function toComparableNumber(value: unknown): Decimal | null {
  if (value == null || value === '') return null;

  try {
    const decimal =
      value instanceof Decimal ? value : new Decimal(value as any);
    return Number.isFinite(decimal.toNumber()) ? decimal : null;
  } catch {
    return null;
  }
}

/**
 * Converte entrambi i valori in Decimal. Ritorna null se anche solo uno dei due
 * non è parsabile: spetta all'operatore decidere come trattare il caso.
 */
export function getComparableNumbers(value: unknown, target: unknown) {
  const numericValue = toComparableNumber(value);
  const numericTarget = toComparableNumber(target);
  if (numericValue == null || numericTarget == null) return null;
  return { numericValue, numericTarget };
}
